import {
  Box,
  Card, CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../../../../components/General/CustomButton';
import CustomModal from '../../../../../components/General/CustomModal';
import apiClient from '../../../../../requests/api/apiClient';
import {
  FarmSchema, NodeSchema, NodeWiseconnSchema,
  SectorSchema, SensorSchema,
} from '../../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../../types';
import {
  setAccountFarm, setAccountFarmsOptions,
} from '../../../../../actions/accountFarmAction';

interface WiseconnZone {
  id: number;
  name: string;
  area: number;
  areaUnit: string;
  polygon: Array<{ lat: number; lng: number }>;
  crops: any[];
  type: string;
}

const SyncDeviceModal = ({
  openSyncDevicesModal, setOpenSyncDevicesModal,
  nodes,
  sensors,
  classes,
  onSync,
}: {
  openSyncDevicesModal: boolean,
  nodes: NodeSchema[],
  sensors: SensorSchema[],
  setOpenSyncDevicesModal: (open: boolean) => void, classes: any,
  onSync?: () => void,
}) => {
  const dispatch = useDispatch();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedAccount } = useSelector(
    (state: ReduxState) => state.userAccount,
  );
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Devices tab state
  const [wiseconnNodes, setWiseconnNodes] = useState<NodeWiseconnSchema[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sensorsSelectedNode, setSensorsSelectedNode] = useState<SensorSchema[]>([]);
  const [existsNodes, setExistsNodes] = useState<NodeSchema[]>([]);
  const [existsSensors, setExistsSensors] = useState<SensorSchema[]>([]);
  const [selectedNodesIds, setSelectedNodesIds] = useState<string[]>([]);
  const [selectedSensorsIds, setSelectedSensorsIds] = useState<string[]>([]);

  // Sectors tab state
  const [wiseconnZones, setWiseconnZones] = useState<WiseconnZone[]>([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [isLoadingZones, setIsLoadingZones] = useState(false);

  const zoneToSector = useMemo(() => {
    const acc: Record<string, { id: string, name: string }> = {};
    selectedFarm?.sectors?.forEach((sector: SectorSchema) => {
      if (sector?.wiseconnId) {
        acc[sector.wiseconnId] = {
          id: sector.id ?? '', name: sector?.name ?? '',
        };
      }
    });
    wiseconnZones.forEach((zone) => {
      const zoneIdStr = String(zone.id);
      if (!acc[zoneIdStr]) {
        acc[zoneIdStr] = { id: '', name: zone.name };
      }
    });
    return acc;
  }, [selectedFarm?.sectors, wiseconnZones]);

  const existingSectorWiseconnIds = useMemo(() => {
    const ids = new Set<string>();
    selectedFarm?.sectors?.forEach((sector: any) => {
      const wid = sector?.wiseconnId ?? sector?.wiseconn_id;
      if (wid) ids.add(String(wid));
    });
    return ids;
  }, [selectedFarm?.sectors]);

  useEffect(() => {
    const getDevices = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.nodes.nodesWiseconnList(
          selectedFarm?.wiseconnId ?? '',
        );
        if (response?.data?.nodes) {
          setWiseconnNodes(response?.data?.nodes);
          const existsNodesFiltered = nodes.filter(
            (node) => response?.data?.nodes?.some(
              (wnode) => String(wnode.id) === String(node.wiseconnId),
            ),
          );
          const existsSensorsFiltered = sensors.filter(
            (sensor) => response?.data?.nodes?.some(
              (wnode) => wnode.sensors?.some(
                (wsensor) => String(wsensor.id) === String(sensor.wiseconnId),
              ),
            ),
          );
          setExistsNodes(existsNodesFiltered);
          setExistsSensors(existsSensorsFiltered);
          if (response?.data?.nodes?.length > 0) {
            setSelectedNodeId(response?.data?.nodes[0].id);
            setSensorsSelectedNode(response?.data?.nodes[0].sensors || []);

            const nodesToSelect = response?.data?.nodes
              .filter((wnode) => !existsNodesFiltered.some(
                (node) => String(node.wiseconnId) === String(wnode.id),
              ))
              .map((wnode) => wnode.id);
            setSelectedNodesIds(nodesToSelect);

            const sensorsToSelect: string[] = [];
            response?.data?.nodes.forEach((wnode) => {
              wnode.sensors?.forEach((sensor) => {
                if (!existsSensorsFiltered.some(
                  (s) => String(s.wiseconnId) === String(sensor.id),
                )) {
                  sensorsToSelect.push(sensor.id);
                }
              });
            });
            setSelectedSensorsIds(sensorsToSelect);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    if (openSyncDevicesModal && selectedFarm?.wiseconnId) {
      getDevices();
    }
  }, [selectedFarm?.wiseconnId, openSyncDevicesModal]);

  useEffect(() => {
    const getZones = async () => {
      setIsLoadingZones(true);
      try {
        const response = await apiClient.customSectors.wiseconnZones(
          selectedFarm?.wiseconnId ?? '',
        );
        if (response?.data?.zones) {
          setWiseconnZones(response.data.zones);
          const newZoneIds = response.data.zones
            .filter((zone) => !existingSectorWiseconnIds.has(String(zone.id)))
            .map((zone) => String(zone.id));
          setSelectedZoneIds(newZoneIds);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingZones(false);
      }
    };
    if (openSyncDevicesModal && selectedFarm?.wiseconnId) {
      getZones();
    }
  }, [selectedFarm?.wiseconnId, openSyncDevicesModal]);

  const handleNodeClick = (node: NodeWiseconnSchema) => {
    setSelectedNodeId(node.id);
    setSensorsSelectedNode(node.sensors || []);
  };

  const handleToggleNode = (nodeId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedNodesIds((prev) => {
      if (prev.includes(nodeId)) {
        return prev.filter((id) => id !== nodeId);
      }
      return [...prev, nodeId];
    });

    const nodeUnselected = wiseconnNodes.find((wnode) => wnode.id === nodeId);
    if (nodeUnselected) {
      setSelectedSensorsIds((prev) => prev.filter(
        (id) => !nodeUnselected.sensors?.some(
          (sensor) => String(sensor.id) === String(id),
        ),
      ));
    }
  };

  const handleToggleSensor = (sensorId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedSensorsIds((prev) => {
      if (prev.includes(sensorId)) {
        return prev.filter((id) => id !== sensorId);
      }
      return [...prev, sensorId];
    });
  };

  const handleToggleZone = (zoneId: string) => {
    setSelectedZoneIds((prev) => {
      if (prev.includes(zoneId)) {
        return prev.filter((id) => id !== zoneId);
      }
      return [...prev, zoneId];
    });
  };

  const convertAreaToHa = (area: number, unit: string) => {
    if (!area) return '0';
    if (unit === 'ha' || unit === 'Ha') return area.toFixed(2);
    if (unit === 'm2' || unit === 'm²') return (area / 10000).toFixed(2);
    return area.toFixed(2);
  };

  const handleSync = async () => {
    setIsLoading(true);
    try {
      // (A5) Step 1-3: Sync sectors first, rebuild zoneToSector map
      const updatedZoneToSector: Record<string, { id: string, name: string }> = {
        ...zoneToSector,
      };

      if (selectedZoneIds.length > 0) {
        const sectorsToSync = wiseconnZones
          .filter((zone) => selectedZoneIds.includes(String(zone.id)))
          .map((zone) => ({
            name: zone.name,
            wiseconnId: String(zone.id),
            farm: selectedFarm?.id ?? '',
            theoreticalSurface: convertAreaToHa(zone.area, zone.areaUnit),
            ...(Array.isArray(zone.polygon) && zone.polygon.length > 0
              ? { coordinates: zone.polygon } : {}),
          }));
        if (sectorsToSync.length > 0) {
          const sectorsResponse = await apiClient.customSectors
            .sectorsSyncBatch(sectorsToSync);

          // (A6) Add newly created sectors to the map
          if (sectorsResponse?.data?.createdSectors) {
            sectorsResponse.data.createdSectors.forEach((sector) => {
              updatedZoneToSector[sector.wiseconnId] = {
                // eslint-disable-next-line
                id: sector._id,
                name: sector.name,
              };
            });
          }
        }
      }

      // (A5) Step 4: Sync nodes
      const hasDevicesToSync = selectedNodesIds.length > 0
        || selectedSensorsIds.length > 0;

      if (hasDevicesToSync) {
        const nodesToSync = wiseconnNodes
          .filter((wnode) => selectedNodesIds.includes(wnode.id))
          .map((wnode) => ({
            name: wnode.name,
            wiseconnId: wnode.id,
            farmId: selectedFarm?.id ?? '',
            coordinates: {
              lng: wnode.lng,
              lat: wnode.lat,
            },
            nodeType: wnode.nodeType,
            isGatewayNode: wnode.isGatewayNode,
            mac: wnode.mac,
            networkNumber: wnode.networkNumber,
          }));

        let createdNodesResponse = null;
        if (nodesToSync.length > 0) {
          createdNodesResponse = await apiClient.customNodes.nodesSyncBatch(
            nodesToSync,
          );
        }

        const wiseconnIdToNodeId: Record<string, string> = {};

        nodes.forEach((node) => {
          if (node.wiseconnId && node.id) {
            wiseconnIdToNodeId[String(node.wiseconnId)] = node.id;
          }
        });

        if (createdNodesResponse?.data) {
          const { createdNodes } = createdNodesResponse.data;
          createdNodes.forEach((node: NodeSchema) => {
            if (node.wiseconnId && node.id) {
              wiseconnIdToNodeId[String(node.wiseconnId)] = node.id;
            }
          });
        }

        // (A5) Step 5: Sync sensors with updated zoneToSector map
        const sensorsToSync = wiseconnNodes
          .flatMap((wnode) => (wnode.sensors || [])
            .filter((sensor) => selectedSensorsIds.includes(sensor.id))
            .map((sensor) => {
              const nodeId = wiseconnIdToNodeId[String(wnode.id)];
              return {
                name: sensor.name,
                wiseconnId: sensor.id,
                farmId: selectedFarm?.id ?? '',
                nodeId,
                sectorId: updatedZoneToSector[
                  String(sensor.zoneId) ?? ''
                ]?.id ?? '',
                sensorType: sensor.sensorType,
                zoneId: sensor.zoneId,
                unit: sensor.unit,
              };
            }));

        if (sensorsToSync.length > 0) {
          await apiClient.customSensors.sensorsSyncBatch(sensorsToSync);
        }
      }

      setOpenSyncDevicesModal(false);
    } catch (error) {
      console.error('Sync error:', error);
      setOpenSyncDevicesModal(false);
    } finally {
      try {
        const rawAccountId = selectedFarm?.accountId;
        const accountId = typeof rawAccountId === 'string'
          ? rawAccountId
          : (rawAccountId as any)?.id
          // eslint-disable-next-line
            ?? (rawAccountId as any)?._id
            ?? selectedAccount?.id ?? '';
        if (accountId) {
          const farmsRes = await apiClient.farms
            .accountsDetail(accountId);
          const allFarms = farmsRes?.data?.allFarms ?? [];
          dispatch(setAccountFarmsOptions(allFarms));
          const freshFarm = allFarms.find(
            (f: FarmSchema) => f.id === selectedFarm?.id
              // eslint-disable-next-line
              || f._id === selectedFarm?.id,
          );
          if (freshFarm) {
            dispatch(setAccountFarm(freshFarm as FarmSchema));
          }
        }
      } catch (err) {
        console.error('[sync] Farm refresh error:', err);
      }
      if (onSync) onSync();
      setIsLoading(false);
    }
  };

  const renderSectorsTab = () => {
    if (isLoadingZones) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '90%',
        }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        {wiseconnZones.map((zone) => {
          const zoneIdStr = String(zone.id);
          const alreadyExists = existingSectorWiseconnIds.has(zoneIdStr);
          return (
            <Card
              key={zone.id}
              sx={{
                mb: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'flex-start',
              }}>
                <Checkbox
                  checked={alreadyExists || selectedZoneIds.includes(zoneIdStr)}
                  onChange={() => handleToggleZone(zoneIdStr)}
                  disabled={alreadyExists}
                  onClick={(e) => e.stopPropagation()}
                />
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600, mb: 0.5,
                  }}>
                    {zone.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    ID: {zone.id}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Superficie teórica:{' '}
                    {convertAreaToHa(zone.area, zone.areaUnit)} Ha
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Superficie real: {zone.area} {zone.areaUnit}
                  </Typography>
                  {zone.type && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Tipo: {zone.type}
                    </Typography>
                  )}
                </CardContent>
              </Box>
              {!alreadyExists ? (
                <Chip
                  color='success'
                  label='Creación'
                  sx={{ marginRight: '5px' }}
                />
              ) : (
                <Chip
                  color='warning'
                  label='Ya existente'
                  sx={{ marginRight: '5px' }}
                />
              )}
            </Card>
          );
        })}
        {wiseconnZones.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}
          >
            No se encontraron zonas en Wiseconn para este campo
          </Typography>
        )}
      </Box>
    );
  };

  const renderDevicesTab = () => {
    if (isLoading) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '90%',
        }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={3} sx={{
        justifyContent: 'center', height: '100%', flexWrap: 'nowrap',
      }}>
        <Grid item xs={6} sx={{
          display: 'flex', flexDirection: 'column', height: '100%',
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Nodos
          </Typography>
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {wiseconnNodes.map((wnode) => (
              <Card
                key={wnode.id}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  cursor: 'pointer',
                  backgroundColor: selectedNodeId === wnode.id
                    ? '#1976d2' : 'background.paper',
                  color: selectedNodeId === wnode.id
                    ? 'white' : 'text.primary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: selectedNodeId === wnode.id
                      ? '#1565c0' : 'action.hover',
                  },
                }}
                onClick={() => handleNodeClick(wnode)}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'flex-start',
                }}>
                  <Checkbox
                    checked={selectedNodesIds.includes(wnode.id)}
                    onChange={(e) => handleToggleNode(wnode.id, e as any)}
                    disabled={existsNodes.some(
                      (node) => String(node.wiseconnId) === String(wnode.id),
                    )}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 600, mb: 0.5,
                    }}>
                      {wnode.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      ID: {wnode.id}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Tipo: {wnode.nodeType}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Mac: {wnode.mac}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Gateway: {wnode.isGatewayNode ? 'Si' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      N° de red: {wnode.networkNumber}
                    </Typography>
                  </CardContent>
                </Box>
                {!existsNodes.some(
                  (node) => String(node.wiseconnId) === String(wnode.id),
                ) ? (
                    <Chip
                      color='success'
                      label='Creación'
                      sx={{ marginRight: '5px' }}
                    />
                  ) : (
                    <Chip
                      color='warning'
                      label='Ya existente'
                      sx={{ marginRight: '5px' }}
                    />
                  )}
              </Card>
            ))}
          </Box>
        </Grid>

        <Grid item xs={6} sx={{
          display: 'flex', flexDirection: 'column', height: '100%',
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Sensores
          </Typography>
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {sensorsSelectedNode.map((sensor) => (
              <Card
                key={sensor.id}
                sx={{
                  mb: 1.5,
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'flex-start',
                }}>
                  <Checkbox
                    checked={selectedSensorsIds.includes(sensor.id)}
                    onChange={
                      (e) => handleToggleSensor(sensor.id, e as any)
                    }
                    disabled={existsSensors.some(
                      (s) => String(s.wiseconnId) === String(sensor.id),
                    )}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" sx={{
                      mb: 1, fontWeight: 600,
                    }}>
                      {sensor.name}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      ID: {sensor.id}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      Sensor: {sensor.sensorType}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      Sector:{' '}
                      {zoneToSector?.[
                        String(sensor.zoneId) ?? ''
                      ]?.name ?? 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      Zone Id: {sensor.zoneId ?? 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      Profundidad: {sensor.sensorDepth ?? 'N/A'}{' '}
                      {sensor.depthUnit ?? ''}
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 0.5, fontSize: '0.875rem',
                    }}>
                      Unidad: {sensor.unit ?? 'N/A'}
                    </Typography>
                  </CardContent>
                </Box>
                {!existsSensors.some(
                  (s) => String(s.wiseconnId) === String(sensor.id),
                ) ? (
                    <Chip
                      color='success'
                      label='Creación'
                      sx={{ marginRight: '5px' }}
                    />
                  ) : (
                    <Chip
                      color='warning'
                      label='Ya existente'
                      sx={{ marginRight: '5px' }}
                    />
                  )}
              </Card>
            ))}
            {(!sensorsSelectedNode
              || sensorsSelectedNode.length === 0) && (
              <Typography variant="body2" sx={{
                color: 'text.secondary', textAlign: 'center', mt: 4,
              }}>
                No hay sensores asociados a este nodo
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <CustomModal
      open={openSyncDevicesModal}
      handleClose={() => setOpenSyncDevicesModal(false)}
      cardClassName={classes.cardModalContainer}
    >
      <Box sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Typography variant="h3" color="primary" sx={{ mb: 2 }}>
          Sincronizar
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Selecciona los sectores o equipos que quieras agregar al campo.
          Al aceptar, se crearán los elementos seleccionados.
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Sectores" />
          <Tab label="Equipos" />
        </Tabs>

        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {activeTab === 0 && renderSectorsTab()}
          {activeTab === 1 && renderDevicesTab()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CustomButton
            buttonText="Cancelar"
            onClick={() => setOpenSyncDevicesModal(false)}
            disabled={isLoading}
            style={{ marginRight: '20px', width: '20%' }}
          />
          <CustomButton
            buttonText="Sincronizar"
            color='lightBlue'
            onClick={handleSync}
            disabled={isLoading}
            style={{ marginLeft: '20px', width: '20%' }}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default SyncDeviceModal;
